import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Box, Building2, ArrowRight, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isDemoMode, getDemoContractIds } from '@/config/demoMode';

interface ContratoBim {
  contrato_id: string;
  contrato_nome: string;
  total_modelos: number;
  total_elementos: number;
}

const BimLandingPage: React.FC = () => {
  const navigate = useNavigate();

  const { data: contratos = [], isLoading } = useQuery({
    queryKey: ['bim-contratos'],
    queryFn: async () => {
      // Get contracts that have BIM uploads
      const { data: uploads, error } = await supabase
        .from('emp_bim_uploads')
        .select('contrato_id')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get unique contract IDs
      let contratoIds = [...new Set((uploads || []).map(u => u.contrato_id))];
      if (isDemoMode()) {
        const demo = new Set(getDemoContractIds());
        contratoIds = contratoIds.filter(id => demo.has(id));
      }

      if (contratoIds.length === 0) return [];

      // Get contract names
      const { data: contratos, error: cErr } = await supabase
        .from('emp_contratos')
        .select('id, nome')
        .in('id', contratoIds);

      if (cErr) throw cErr;

      // Count models and elements per contract
      const result: ContratoBim[] = [];
      for (const c of contratos || []) {
        const modelos = (uploads || []).filter(u => u.contrato_id === c.id).length;

        const { count } = await supabase
          .from('emp_bim_elementos')
          .select('*', { count: 'exact', head: true })
          .eq('contrato_id', c.id);

        result.push({
          contrato_id: c.id,
          contrato_nome: c.nome,
          total_modelos: modelos,
          total_elementos: count || 0,
        });
      }

      return result;
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Visualização BIM</h1>
          <p className="text-sm text-muted-foreground mt-1">Modelos 3D dos empreendimentos</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-40 w-full rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Visualização BIM</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Modelos 3D dos empreendimentos — selecione um contrato para visualizar
          </p>
        </div>
      </div>

      {contratos.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Box className="h-16 w-16 text-muted-foreground/30 mb-4" />
            <h2 className="text-lg font-semibold mb-2">Nenhum modelo BIM disponível</h2>
            <p className="text-sm text-muted-foreground max-w-md mb-4">
              Para começar, acesse um contrato e faça upload de um arquivo IFC ou NWD na aba BIM.
            </p>
            <Button variant="outline" onClick={() => navigate('/empreendimentos/contratos')}>
              <Building2 className="h-4 w-4 mr-2" />
              Ver Contratos
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {contratos.map(c => (
            <Card
              key={c.contrato_id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/empreendimentos/contratos/${c.contrato_id}/bim`)}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Box className="h-4 w-4 text-primary" />
                  {c.contrato_nome}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 mb-3">
                  <Badge variant="secondary" className="text-xs">
                    <Upload className="h-3 w-3 mr-1" />
                    {c.total_modelos} modelo{c.total_modelos !== 1 ? 's' : ''}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {c.total_elementos} elemento{c.total_elementos !== 1 ? 's' : ''}
                  </Badge>
                </div>
                <Button variant="ghost" size="sm" className="w-full justify-between text-primary">
                  Abrir Visualizador 3D
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BimLandingPage;
